/**
 * React Query hooks.
 *
 * The license toggle is optimistic with rollback: the switch flips instantly,
 * the mutation goes out, and the server's row replaces the guess. That is the
 * core interaction of the entry screen, so it is worth the extra care.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiError } from '@/api/client';
import { api } from '@/api/client';
import { useEnvironment } from '@/environment/EnvironmentProvider';
import type {
  AuditEntry,
  BulkLicenseChange,
  BulkLicenseResponse,
  CredentialGroup,
  CredentialSlot,
  CredentialTestResult,
  EnvironmentInfo,
  LicenseCopyPlan,
  LicenseCell,
  LicenseCellState,
  LicenseMatrix,
  OverrideWrite,
  Page,
  SyncRun,
  TenantSummary,
  UserAccessRow,
  UserAssignResult,
  UserKickout,
  UserKickoutResult,
  SepEnvironment,
} from '@/types/api';

/**
 * Every cache key is scoped to the environment (doc 18).
 *
 * The environment is a **required argument**, not something these read for
 * themselves. An earlier version used getters that called `currentEnvironment()`
 * — which produced correct keys and a page that never updated: reading the store
 * is not subscribing to it, so a hook whose component ignored the context never
 * re-rendered, never recomputed its key, and went on showing the environment you
 * had just switched away from. Only Lizenzvergabe worked, and only because it
 * happened to call `useEnvironment()` for something else.
 *
 * Passing it in makes the subscription a type error to omit: to call these you
 * need an environment, and the only way to get one in a component is
 * `useEnvironment()`, which subscribes.
 *
 * `scope` is the common prefix, so invalidating it drops everything belonging to
 * one environment in a single call.
 */
export const queryKeys = {
  scope: (env: SepEnvironment) => ['env', env] as const,
  matrix: (env: SepEnvironment) => ['env', env, 'licenses', 'matrix'] as const,
  tenants: (env: SepEnvironment) => ['env', env, 'tenants'] as const,
  tenantUsers: (env: SepEnvironment, key: string, q: string) =>
    ['env', env, 'tenants', key, 'users', q] as const,
  credentials: (env: SepEnvironment) => ['env', env, 'credentials'] as const,
  credentialGroups: (env: SepEnvironment) =>
    ['env', env, 'credentials', 'groups'] as const,
  syncRuns: (env: SepEnvironment) => ['env', env, 'sync', 'runs'] as const,
  audit: (env: SepEnvironment, filters: string) =>
    ['env', env, 'audit', filters] as const,
  copyPreview: (env: SepEnvironment) =>
    ['env', env, 'licenses', 'copy-preview'] as const,
  users: (env: SepEnvironment, params: string) =>
    ['env', env, 'users', params] as const,
  userRoles: (env: SepEnvironment) => ['env', env, 'users', 'roles'] as const,
  // Two things that are the same whatever you are looking at, so deliberately
  // unscoped: the list of installations, and the app catalogue — one product
  // list serves all three (doc 18).
  environments: ['environments'] as const,
  apps: ['apps'] as const,
};

export function useLicenseMatrix(): UseQueryResult<LicenseMatrix, ApiError> {
  const environment = useEnvironment();
  return useQuery({
    queryKey: queryKeys.matrix(environment),
    queryFn: ({ signal }) => api.get<LicenseMatrix>('/licenses/matrix', signal),
    staleTime: 30_000,
  });
}

export interface ToggleLicenseVars {
  tenantKey: string;
  appKey: string;
  enabled: boolean;
  expectedUpdatedAt: string | null;
  notes?: string | null;
}

interface MatrixSnapshot {
  previous: LicenseMatrix | undefined;
}

function patchCell(
  matrix: LicenseMatrix,
  tenantKey: string,
  appKey: string,
  next: LicenseCellState,
): LicenseMatrix {
  return {
    ...matrix,
    tenants: matrix.tenants.map((tenant) =>
      tenant.key === tenantKey
        ? { ...tenant, licenses: { ...tenant.licenses, [appKey]: next } }
        : tenant,
    ),
  };
}

export function useToggleLicense(): UseMutationResult<
  LicenseCell,
  ApiError,
  ToggleLicenseVars,
  MatrixSnapshot
> {
  const environment = useEnvironment();
  const queryClient = useQueryClient();

  return useMutation<LicenseCell, ApiError, ToggleLicenseVars, MatrixSnapshot>({
    mutationFn: ({ tenantKey, appKey, enabled, expectedUpdatedAt, notes }) =>
      api.put<LicenseCell>(`/licenses/${tenantKey}/${appKey}`, {
        enabled,
        expectedUpdatedAt,
        notes,
      }),

    onMutate: async ({ tenantKey, appKey, enabled }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.matrix(environment) });
      const previous = queryClient.getQueryData<LicenseMatrix>(queryKeys.matrix(environment));
      if (previous) {
        const current = previous.tenants.find((t) => t.key === tenantKey)?.licenses[appKey];
        const optimistic: LicenseCell = current
          ? { ...current, enabled, isActive: enabled }
          : {
              enabled,
              validFrom: null,
              validUntil: null,
              seats: null,
              source: 'manual',
              notes: null,
              overrideCount: { allow: 0, deny: 0 },
              updatedAt: new Date().toISOString(),
              updatedBy: null,
              isActive: enabled,
              reason: enabled ? 'tenant_license' : 'tenant_license_off',
            };
        queryClient.setQueryData(
          queryKeys.matrix(environment),
          patchCell(previous, tenantKey, appKey, optimistic),
        );
      }
      return { previous };
    },

    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(queryKeys.matrix(environment), context.previous);
    },

    onSuccess: (cell, { tenantKey, appKey }) => {
      // The server's row is authoritative — especially `updatedAt`, which the
      // next optimistic-concurrency check is compared against.
      const current = queryClient.getQueryData<LicenseMatrix>(queryKeys.matrix(environment));
      if (current) {
        queryClient.setQueryData(queryKeys.matrix(environment), patchCell(current, tenantKey, appKey, cell));
      }
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.matrix(environment) });
    },
  });
}

export function useBulkLicenses(): UseMutationResult<
  BulkLicenseResponse,
  ApiError,
  { changes: BulkLicenseChange[]; notes?: string }
> {
  const environment = useEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => api.post<BulkLicenseResponse>('/licenses/bulk', payload),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.matrix(environment) });
    },
  });
}

export function useTenantUsers(
  tenantKey: string,
  q: string,
  enabled: boolean,
): UseQueryResult<Page<UserAccessRow>, ApiError> {
  const environment = useEnvironment();
  return useQuery({
    queryKey: queryKeys.tenantUsers(environment, tenantKey, q),
    queryFn: ({ signal }) =>
      api.get<Page<UserAccessRow>>(
        `/tenants/${tenantKey}/users?limit=100${q ? `&q=${encodeURIComponent(q)}` : ''}`,
        signal,
      ),
    enabled,
  });
}

export interface OverrideVars extends OverrideWrite {
  username: string;
  appKey: string;
}

export function useSetOverride(): UseMutationResult<unknown, ApiError, OverrideVars> {
  const environment = useEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ username, appKey, ...body }) =>
      api.put(`/overrides/${encodeURIComponent(username)}/${appKey}`, body),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.matrix(environment) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.tenants(environment) });
    },
  });
}

export function useDeleteOverride(): UseMutationResult<
  unknown,
  ApiError,
  { username: string; appKey: string }
> {
  const environment = useEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ username, appKey }) =>
      api.delete(`/overrides/${encodeURIComponent(username)}/${appKey}`),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.matrix(environment) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.tenants(environment) });
    },
  });
}

export function useTenants(): UseQueryResult<TenantSummary[], ApiError> {
  const environment = useEnvironment();
  return useQuery({
    queryKey: queryKeys.tenants(environment),
    queryFn: ({ signal }) => api.get<TenantSummary[]>('/tenants', signal),
  });
}

export function useSyncRuns(): UseQueryResult<SyncRun[], ApiError> {
  const environment = useEnvironment();
  return useQuery({
    queryKey: queryKeys.syncRuns(environment),
    queryFn: ({ signal }) => api.get<SyncRun[]>('/sync/runs?limit=1', signal),
  });
}

export function useSyncUsers(): UseMutationResult<SyncRun, ApiError, void> {
  const environment = useEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<SyncRun>('/sync/users'),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.matrix(environment) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.syncRuns(environment) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.tenants(environment) });
    },
  });
}

export function useCredentials(): UseQueryResult<CredentialSlot[], ApiError> {
  const environment = useEnvironment();
  return useQuery({
    queryKey: queryKeys.credentials(environment),
    queryFn: ({ signal }) => api.get<CredentialSlot[]>('/credentials', signal),
  });
}

export function useCredentialGroups(): UseQueryResult<CredentialGroup[], ApiError> {
  const environment = useEnvironment();
  return useQuery({
    queryKey: queryKeys.credentialGroups(environment),
    queryFn: ({ signal }) => api.get<CredentialGroup[]>('/credentials/groups', signal),
  });
}

export function useSaveCredential(): UseMutationResult<
  CredentialSlot,
  ApiError,
  { key: string; secret: string }
> {
  const environment = useEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, secret }) => api.put<CredentialSlot>(`/credentials/${key}`, { secret }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.credentials(environment) });
    },
  });
}

export function useTestCredential(): UseMutationResult<CredentialTestResult, ApiError, string> {
  const environment = useEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (key) => api.post<CredentialTestResult>(`/credentials/${key}/test`),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.credentials(environment) });
    },
  });
}

export function useAuditLog(filters: string): UseQueryResult<Page<AuditEntry>, ApiError> {
  const environment = useEnvironment();
  return useQuery({
    queryKey: queryKeys.audit(environment, filters),
    queryFn: ({ signal }) => api.get<Page<AuditEntry>>(`/audit?${filters}`, signal),
  });
}

/**
 * The installations this dashboard can administer, and what each is missing.
 *
 * Not the same as the hard-coded list in `environment/store.ts`: that one knows
 * the three names and their colours without a round trip, so the app bar renders
 * before any request lands. This one reports whether each is actually usable —
 * a host that was never configured cannot be synchronised, and the switcher says
 * so rather than letting someone pick it and meet a 503.
 */
export function useEnvironments(): UseQueryResult<EnvironmentInfo[], ApiError> {
  // The one hook with no environment in its key: the list of installations is
  // the same whichever one you are looking at.
  return useQuery({
    queryKey: queryKeys.environments,
    queryFn: ({ signal }) => api.get<EnvironmentInfo[]>('/environments', signal),
    staleTime: Infinity,
  });
}

/**
 * What "Mit Production synchronisieren" would change, for the confirmation
 * dialog. Never cached: the numbers being confirmed must describe the database
 * as it is now, not as it was when the dialog was last opened.
 */
export function useCopyPreview(enabled: boolean): UseQueryResult<LicenseCopyPlan, ApiError> {
  const environment = useEnvironment();
  return useQuery({
    queryKey: queryKeys.copyPreview(environment),
    queryFn: ({ signal }) =>
      api.get<LicenseCopyPlan>('/licenses/copy-from-production/preview', signal),
    enabled,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useCopyFromProduction(): UseMutationResult<LicenseCopyPlan, ApiError, void> {
  const environment = useEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<LicenseCopyPlan>('/licenses/copy-from-production'),
    onSettled: () => {
      // The whole environment changed, so drop all of its cached views rather
      // than guessing which ones the overwrite touched.
      void queryClient.invalidateQueries({ queryKey: queryKeys.scope(environment) });
    },
  });
}

export interface KickoutVars extends UserKickout {
  username: string;
}

/**
 * Delete a SEP account permanently. No undo, on either side.
 *
 * Not optimistic, unlike the licence toggle: an optimistic update shows the
 * result before the server has agreed, and the one case where that is
 * unacceptable is the one where the action cannot be taken back. The row keeps
 * its old state until SEP has actually accepted.
 *
 * Invalidates the whole environment rather than the user list alone — a deleted
 * account changes tenant user counts and the licence matrix's override badges
 * too, and guessing which of those to refresh is how a stale screen convinces
 * someone the deletion failed.
 */
export function useKickoutUser(): UseMutationResult<UserKickoutResult, ApiError, KickoutVars> {
  const environment = useEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ username, ...body }) =>
      api.post<UserKickoutResult>(`/users/${encodeURIComponent(username)}/kickout`, body),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.scope(environment) });
    },
  });
}

/**
 * Add a read-only directory user to SEP's `assigned_users` — the "Assign Users"
 * tool — so their tenant and role become editable here.
 *
 * Invalidates the environment like kickout: the row gains an id and a role, and
 * SEP may report a different tenant than ours, which moves the user.
 */
export function useAssignUser(): UseMutationResult<UserAssignResult, ApiError, string> {
  const environment = useEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (username) =>
      api.post<UserAssignResult>(`/users/${encodeURIComponent(username)}/assign`),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.scope(environment) });
    },
  });
}
